/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

import { Vector3 } from 'three';
import { Atom } from '../models/Atom';
import { Defect, Dopant, LatticeParameters } from '../types';
import { fractionalToCartesian, latticeVectors } from '../crystal/Crystal';

// Apply a doping recipe to an already-expanded supercell. Picks specific
// host atoms at random using a deterministic PRNG seeded from
// (crystalName, nx, ny, nz) so reloading the same crystal at the same
// supercell size always shows the same dopant placements — important for
// teaching ("compare this configuration with mine") and for reproducible
// Predict calls.
//
// Mutates `atoms` in place: substituted entries get replaced with freshly
// constructed `Atom`s carrying the dopant element. The new atoms inherit
// the original atom's index and position, and the constructor re-derives
// mass / sigma / epsilon from the dopant element so MD parameters track
// the substitution.
//
// `buildCrystalScaffold` calls this right after expansion and before the
// scratch-vector init loop, so displacement / acceleration / initial*
// fields are seeded uniformly across host + dopant atoms.

const mulberry32 = (seed: number): (() => number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

// Cheap FNV-1a hash to fold the seed string into a uint32. The exact hash
// is unimportant — we only need a stable string→number map.
const hashSeedString = (s: string): number => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
};

export const applyDopants = (
  atoms: Atom[],
  dopants: Dopant[],
  crystalName: string,
  nx: number,
  ny: number,
  nz: number,
): void => {
  if (!dopants || dopants.length === 0) return;

  // One RNG per call, seeded deterministically. Each dopant pass advances
  // the same generator so reorderings of the dopants array DO change
  // placements (which is fine — reorder = different recipe).
  const rng = mulberry32(hashSeedString(`${crystalName}|${nx}x${ny}x${nz}`));

  // Track indices already substituted in earlier passes so two dopants
  // targeting the same host don't fight over the same site. Without this
  // a second pass could pick a site the first pass already converted —
  // the host-element filter below would skip it (it's no longer host),
  // but reserving up front is clearer and keeps placement counts honest.
  const claimed = new Set<number>();

  for (const dop of dopants) {
    const conc = dop.concentrationAtPct;
    if (!Number.isFinite(conc) || conc <= 0) continue;

    // Candidate sites: atoms still on the host element that haven't been
    // claimed by an earlier dopant pass.
    const candidates: number[] = [];
    for (let i = 0; i < atoms.length; i++) {
      if (claimed.has(i)) continue;
      if (atoms[i].elementSymbol === dop.hostElement) candidates.push(i);
    }
    if (candidates.length === 0) continue;

    // Target count: at.% of HOSTS substituted. Round to nearest; floor()
    // would silently drop every dopant below 1/hostCount, which is the
    // common pedagogical case (0.1% in a 64-atom supercell ≈ 0.06 atoms
    // ⇒ floor=0 ⇒ no visible dopant). Math.round gives a more useful
    // first impression — small supercells still show one dopant at low
    // concentrations, big supercells show the precise scaled count.
    let count = Math.round((conc / 100) * candidates.length);
    if (count < 1) count = 1;
    if (count > candidates.length) count = candidates.length;

    // Fisher-Yates partial shuffle: pick `count` distinct candidates.
    for (let pickIdx = 0; pickIdx < count; pickIdx++) {
      const j = pickIdx + Math.floor(rng() * (candidates.length - pickIdx));
      [candidates[pickIdx], candidates[j]] = [candidates[j], candidates[pickIdx]];
      const atomIdx = candidates[pickIdx];
      claimed.add(atomIdx);
      // Re-construct with the dopant element so mass / sigma / epsilon
      // come from the dopant's UFF / element table. Preserve index and
      // position (a fresh Vector3 to avoid sharing the basis-atom's
      // position reference with the substituted-out copy).
      const old = atoms[atomIdx];
      const pos = new Vector3(old.position.x, old.position.y, old.position.z);
      atoms[atomIdx] = new Atom(old.index, dop.dopantElement, pos);
    }
  }
};

// Apply a defects recipe to an already-expanded supercell. Two kinds:
//   - 'vacancy' — pick host atoms of the listed element and REMOVE them
//   - 'interstitial' — INSERT new atoms of the listed element at random
//                      fractional positions inside the supercell
//
// Returns a fresh array (vacancies shrink the basis, interstitials grow
// it — neither composes cleanly with in-place mutation of an existing
// const array in the caller). Atom.index is renumbered sequentially on
// the result so downstream code that relies on the array-position-equals-
// index invariant (bond indexing, molecule labeling) stays correct.
//
// Uses its own seeded PRNG (different seed string than applyDopants so
// the two passes don't share random draws and reorderings stay
// independent) but otherwise mirrors the same deterministic-by-supercell
// pattern.
export const applyDefects = (
  atoms: Atom[],
  defects: Defect[],
  crystalName: string,
  nx: number,
  ny: number,
  nz: number,
  lattice: LatticeParameters,
): Atom[] => {
  if (!defects || defects.length === 0) return atoms;

  const rng = mulberry32(hashSeedString(`${crystalName}|defects|${nx}x${ny}x${nz}`));

  // Pass 1: vacancies. Collect indices to remove (across all vacancy
  // entries), then filter the array in a single pass at the end. Doing
  // it as a set lets multiple vacancy entries co-exist (e.g. Si + C
  // vacancies in SiC) without one pass invalidating the other.
  const toRemove = new Set<number>();
  for (const d of defects) {
    if (d.kind !== 'vacancy') continue;
    if (!Number.isFinite(d.concentrationAtPct) || d.concentrationAtPct <= 0) continue;

    const candidates: number[] = [];
    for (let i = 0; i < atoms.length; i++) {
      if (toRemove.has(i)) continue;
      if (atoms[i].elementSymbol === d.element) candidates.push(i);
    }
    if (candidates.length === 0) continue;

    let count = Math.round((d.concentrationAtPct / 100) * candidates.length);
    if (count < 1) count = 1;
    if (count > candidates.length) count = candidates.length;

    for (let pickIdx = 0; pickIdx < count; pickIdx++) {
      const j = pickIdx + Math.floor(rng() * (candidates.length - pickIdx));
      [candidates[pickIdx], candidates[j]] = [candidates[j], candidates[pickIdx]];
      toRemove.add(candidates[pickIdx]);
    }
  }

  let result: Atom[];
  if (toRemove.size === 0) {
    result = atoms;
  } else {
    result = atoms.filter((_, i) => !toRemove.has(i));
    // Renumber Atom.index sequentially. Downstream consumers
    // (computeBonds, labelMolecules) work off array position, but
    // serialization paths and the gallery thumbnail occasionally read
    // Atom.index — keep the invariant tight to avoid surprises.
    for (let i = 0; i < result.length; i++) result[i].index = i;
  }

  // Pass 2: interstitials. Fractional coordinates uniform in
  // [0, nx) × [0, ny) × [0, nz), converted to Cartesian via the
  // lattice vectors. Concentration is interpreted relative to the
  // current count of host-species atoms (POST-vacancy) so a 1% Si
  // interstitial recipe scales with the remaining Si lattice.
  const vectors = latticeVectors(lattice);
  for (const d of defects) {
    if (d.kind !== 'interstitial') continue;
    if (!Number.isFinite(d.concentrationAtPct) || d.concentrationAtPct <= 0) continue;

    let hostCount = 0;
    for (const a of result) if (a.elementSymbol === d.element) hostCount++;
    if (hostCount === 0) continue;

    let count = Math.round((d.concentrationAtPct / 100) * hostCount);
    if (count < 1) count = 1;

    for (let k = 0; k < count; k++) {
      const fx = rng() * nx;
      const fy = rng() * ny;
      const fz = rng() * nz;
      const pos = fractionalToCartesian(fx, fy, fz, vectors);
      result.push(new Atom(result.length, d.element, pos));
    }
  }

  return result;
};
